import _ from 'lodash'
import moment from 'moment'

import React, { useEffect, useRef, useState } from 'react'
import { Field, FieldArray, FormSection, InjectedFormProps, change, getFormValues, reduxForm } from 'redux-form'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import {
  BEFORE_AFTER_EATING,
  BEFORE_AFTER_EATING_OPTIONS,
  FREQUENCIES,
  FREQUENCY_OPTIONS,
} from 'services/medicationForms/constants'
import { STATUS_OPTIONS_VALUES } from 'services/users/constants'
import { Option } from 'constants/models'
import { Option as DropdownOption } from 'components/InfiniteDropdowns/InfiniteDropdownsModel'
import { NEUTRAL_COLOURS } from 'constants/colors'
import { SelectedImageProps } from 'components/PhotoUpload/PhotoUpload'
import { Nursery } from 'services/nurseries/models'

import {
  isMaxLengthLessThan,
  isNumberLessOrEqualThan,
  isRequired,
  isStartDateBeforeEndDate,
} from 'utils/fieldValidation'
import { generateRoute } from 'utils/routing'
import { createTypeFromEnumValues } from 'utils/typescript'

import { Banner, Button, Form, Grid, Section, Spinner, Typography } from 'components'

import i18n from 'translations'

import { StyledFormContentContainer } from './AddFormStyled'

const isMaxLengthLessThan50 = isMaxLengthLessThan(50)

const DISABLED_HOURS = [0, 1, 2, 3, 4, 5, 19, 20, 21, 22, 23]

const renderAdministrationTimeItem = (item, i, fields) => (
  <Form.Row margin="0">
    <Grid>
      <Grid.Item width={{ desktop: 'auto', mobile: 'calc(100% - 50px)' }}>
        <Field
          component={Form.TimePicker}
          disabledHours={() => DISABLED_HOURS}
          local={false}
          name={`${item}.time`}
          placeholder={i18n.t(
            'module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:AdministrationTime:placeholder',
          )}
          validate={isRequired}
        />
      </Grid.Item>
      <Grid.Item width={{ desktop: '50px' }}>
        {0 !== i && (
          <Form.RemoveRowButton
            trash
            onClick={() => fields.remove(i)}
          />
        )}
      </Grid.Item>
    </Grid>
  </Form.Row>
)

const renderAdministrationTimes = ({ fields }) => (
  <React.Fragment>
    {fields.map((item, i) => renderAdministrationTimeItem(item, i, fields))}
    <Button.ActionButton
      label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:AdministrationTime:addButtonLabel')}
      onClick={() => fields.push({ time: null })}
    />
  </React.Fragment>
)

interface AdministrationTimeItem {
  time?: number
}

interface MedicationDetails {
  beforeAfterEatingHours?: number
  beforeAfterEatingMinutes?: number
  beforeOrAfterEating?: Option
  dose?: string
  expiryDate?: Date | string
  frequency?: createTypeFromEnumValues<FREQUENCIES>
  howLongBeforeOrAfterEating?: string
  id?: number
  medication?: string
  methodOfAdministration?: string
  reason?: string
  sideEffects?: string
  storageInstructions?: string
  storeOnSite?: boolean
  timesOfAdministration?: AdministrationTimeItem[]
}

export interface AddMedicationFormVales {
  child?: DropdownOption
  endDate?: Date
  medicationDetails?: MedicationDetails
  otherNotes?: string
  parent?: Option
  parentSignature?: SelectedImageProps
  reviewDate?: Date
  staffReceivingMedication?: Option
  staffReceivingMedicationSignature?: SelectedImageProps
  startDate?: Date
}

export const ADD_MEDICATION_FORM = 'AddMedicationForm'

const mapState = (state) => ({
  formValues: getFormValues(ADD_MEDICATION_FORM)(state) as AddMedicationFormVales,
})

const mapDispatch = {
  changeValue: (field, value) => change(ADD_MEDICATION_FORM, field, value),
}

const connector = connect(mapState, mapDispatch)

type PropsFromRedux = ConnectedProps<typeof connector>

type AddFormProps = {
  childId: number
  isChildContext?: boolean
  isDraftSubmitting?: boolean
  isLongTerm?: boolean
  isSubmitting?: boolean
  nursery: Nursery
  onSubmit: (fields: AddMedicationFormVales) => void
  onSubmitAsDraft?: () => void
} & PropsFromRedux

const AddForm: React.FC<InjectedFormProps<AddMedicationFormVales, AddFormProps> & AddFormProps> = ({
  changeValue,
  childId,
  formValues,
  handleSubmit,
  isChildContext,
  isDraftSubmitting,
  isLongTerm,
  isSubmitting,
  nursery,
  onSubmit,
  onSubmitAsDraft,
}) => {
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const [isParentDropdownRendering, setIsParentDropdownRendering] = useState<boolean>(false)

  const { child, medicationDetails = {}, parent, staffReceivingMedication } = formValues || {}
  const { beforeOrAfterEating, frequency, medication, storeOnSite } = medicationDetails
  const enableButtons = child && medication

  const staffSignatureRef = useRef<any>()
  const parentSignatureRef = useRef<any>()

  useEffect(() => {
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (isInitialized && staffSignatureRef.current) {
      staffSignatureRef.current.clearSignature()
    }

    if (!staffReceivingMedication) {
      changeValue('staffReceivingMedicationSignature', null)
    }
  }, [staffReceivingMedication])

  useEffect(() => {
    if (isInitialized && parentSignatureRef.current) {
      parentSignatureRef.current.clearSignature()
    }

    if (!parent) {
      changeValue('parentSignature', null)
    }
  }, [parent])

  useEffect(() => {
    if (isInitialized) {
      changeValue('parent', null)

      if (parentSignatureRef.current) {
        parentSignatureRef.current.clearSignature()
      }

      setIsParentDropdownRendering(true)
    }
  }, [child])

  useEffect(() => {
    if (isParentDropdownRendering) {
      setTimeout(() => setIsParentDropdownRendering(false))
    }
  }, [isParentDropdownRendering])

  const renderSignatureDetail = () => (
    <Section.SubSection title={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:title')}>
      <Typography margin="15px 0">
        {i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:infoText')}
      </Typography>
      <Form.Row
        label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:StaffReceivingMedication:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          component={Form.InfiniteDropdowns.Memberships}
          name="staffReceivingMedication"
          placeholder={
            i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:StaffReceivingMedication:placeholder')
          }
          status={STATUS_OPTIONS_VALUES.ACTIVE}
          validate={isRequired}
          searchable
        />
      </Form.Row>
      {staffReceivingMedication && (
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:StaffSignatures:label')}
          required
          verticalLabel
        >
          <Field
            component={Form.SignaturePad}
            name="staffReceivingMedicationSignature"
            signatureRef={staffSignatureRef}
            validate={isRequired}
            searchable
          />
        </Form.Row>
      )}
      <Form.Row
        label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:Parent:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        {isParentDropdownRendering
          ? <Spinner size="small" />
          : (
            <Field
              childId={child?.value}
              component={Form.InfiniteDropdowns.Carers}
              disabled={!child}
              name="parent"
              placeholder={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:Parent:placeholder')}
              rawValue={parent}
              status={STATUS_OPTIONS_VALUES.ACTIVE}
              validate={isRequired}
              parentalResponsibilityOnly
              searchable
            />
          )}
      </Form.Row>
      <Typography color={NEUTRAL_COLOURS.GRAY} margin="15px 0" italic>
        {i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:parentConcent', {
          nurseryName: nursery?.name,
        })}
      </Typography>
      {parent && (
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:ParentSignature:label')}
          required
          verticalLabel
        >
          <Field
            component={Form.SignaturePad}
            disabled={!parent}
            name="parentSignature"
            signatureRef={parentSignatureRef}
            validate={isRequired}
            searchable
          />
        </Form.Row>
      )}
      <Banner.Info>
        {i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:bannerInfo')}
      </Banner.Info>
      <Banner.Info>
        {i18n.t('module:Safeguarding:MedicationForm:Add:Form:Signatures:draftSignatureInfo')}
      </Banner.Info>
    </Section.SubSection>
  )

  const renderStockDetail = () => (
    <Section.SubSection title={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Stock:title')}>
      <FormSection name="medicationDetails">
        <Form.Row margin="0">
          <Field
            component={Form.Checkbox}
            label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Stock:StoreMedicationOnSite:label')}
            name="storeOnSite"
          />
        </Form.Row>
        {storeOnSite && (
          <Form.Row
            label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:Stock:StorageInstructions:label')}
            width={{ field: '100%' }}
            verticalLabel
          >
            <Field
              component={Form.TextAreaField}
              name="storageInstructions"
            />
          </Form.Row>
        )}
      </FormSection>
    </Section.SubSection>
  )

  const renderMedicationDetail = () => (
    <Section.SubSection title={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:title')}>
      <FormSection name="medicationDetails">
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:Medication:label')}
          width={{ field: '300px' }}
          required
          verticalLabel
        >
          <Field
            component={Form.TextField}
            name="medication"
            placeholder={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:Medication:placeholder')}
            validate={[isRequired, isMaxLengthLessThan50]}
          />
        </Form.Row>
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:ReasonForMedication:label')}
          width={{ field: '100%' }}
          required
          verticalLabel
        >
          <Field
            component={Form.TextAreaField}
            name="reason"
            placeholder={i18n.t(
              'module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:ReasonForMedication:placeholder',
            )}
            validate={isRequired}
          />
        </Form.Row>
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:MethodOfAdministration:label')}
          width={{ field: '300px' }}
          required
          verticalLabel
        >
          <Field
            component={Form.TextField}
            name="methodOfAdministration"
            placeholder={i18n.t(
              'module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:MethodOfAdministration:placeholder',
            )}
            validate={isRequired}
          />
        </Form.Row>
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:Dose:label')}
          width={{ field: '300px' }}
          required
          verticalLabel
        >
          <Field
            component={Form.TextField}
            name="dose"
            placeholder={i18n.t(
              'module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:Dose:placeholder',
            )}
            validate={isRequired}
          />
        </Form.Row>
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:Frequency:label')}
          required
          verticalLabel
        >
          <Field
            component={Form.RadioGroup}
            name="frequency"
            options={FREQUENCY_OPTIONS}
            validate={isRequired}
            buttonStyle
            horizontal
          />
        </Form.Row>
        {frequency === FREQUENCIES.DAILY && (
          <Form.Row
            label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:AdministrationTime:label')}
            required
            verticalLabel
          >
            <FieldArray
              component={renderAdministrationTimes}
              name="timesOfAdministration"
            />
          </Form.Row>
        )}
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:BeforeAfterEating:label')}
          width={{ field: '300px' }}
          required
          verticalLabel
        >
          <Field
            component={Form.Select}
            name="beforeOrAfterEating"
            options={BEFORE_AFTER_EATING_OPTIONS}
            placeholder={i18n.t(
              'module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:BeforeAfterEating:placeholder',
            )}
            validate={isRequired}
          />
        </Form.Row>
        {
          (
            beforeOrAfterEating?.value === BEFORE_AFTER_EATING.AFTER_EATING
            || beforeOrAfterEating?.value === BEFORE_AFTER_EATING.BEFORE_EATING
          ) && (
            <Form.Row
              label={
                i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:BeforeAfterEatingTime:label')
              }
              width={{ field: 'auto' }}
              required
              verticalLabel
            >
              <Grid>
                <Grid.Item width={{ desktop: '100px', mobile: 'calc(100% - 50px)' }}>
                  <Field
                    component={Form.TextField}
                    name="beforeAfterEatingHours"
                    placeholder={i18n.t(
                      'module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:BeforeAfterEatingTime:placeholder',
                    )}
                    suffix={i18n.t('global:hours')}
                    type="number"
                    validate={isNumberLessOrEqualThan(23)}
                    hideNumberArrow
                  />
                </Grid.Item>
                <Grid.Item width={{ desktop: '100px', mobile: 'calc(100% - 50px)' }}>
                  <Field
                    component={Form.TextField}
                    name="beforeAfterEatingMinutes"
                    placeholder={i18n.t(
                      'module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:BeforeAfterEatingTime:placeholder',
                    )}
                    suffix={i18n.t('global:minutes')}
                    type="number"
                    validate={isNumberLessOrEqualThan(59)}
                    hideNumberArrow
                  />
                </Grid.Item>
              </Grid>
            </Form.Row>
          )
        }
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:ExpiryDate:label')}
          required
          verticalLabel
        >
          <Field
            component={Form.DatePicker}
            name="expiryDate"
            validate={isRequired}
          />
        </Form.Row>
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:SideEffects:label')}
          width={{ field: '100%' }}
          verticalLabel
        >
          <Field
            component={Form.TextAreaField}
            name="sideEffects"
          />
        </Form.Row>
      </FormSection>
      <Form.Row
        label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:MedicationDetails:OtherNotes:label')}
        width={{ field: '100%' }}
        verticalLabel
      >
        <Field
          component={Form.TextAreaField}
          name="otherNotes"
        />
      </Form.Row>
    </Section.SubSection>
  )

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <StyledFormContentContainer>
        <Form.Row
          label={_.upperFirst(i18n.t('global:child'))}
          width={{ field: '300px' }}
          required
          verticalLabel
        >
          <Field
            component={Form.InfiniteDropdowns.Children}
            disabled={isChildContext}
            name="child"
            validate={isRequired}
          />
        </Form.Row>
        <Grid>
          <Grid.Item width={{ desktop: 'auto', mobile: 'calc(100% - 50px)' }}>
            <Form.Row
              label={_.upperFirst(i18n.t('global:startDate'))}
              required
              verticalLabel
            >
              <Field
                component={Form.DatePicker}
                name="startDate"
                validate={isRequired}
              />
            </Form.Row>
          </Grid.Item>
          <Grid.Item width={{ desktop: 'auto', mobile: 'calc(100% - 50px)' }}>
            <Form.Row
              label={_.upperFirst(i18n.t('global:endDate'))}
              required
              verticalLabel
            >
              <Field
                component={Form.DatePicker}
                name="endDate"
                validate={isRequired}
              />
            </Form.Row>
          </Grid.Item>
        </Grid>
        {isLongTerm && (
          <Form.Row
            label={i18n.t('module:Safeguarding:MedicationForm:Add:Form:ReviewDate:label')}
            required
            verticalLabel
          >
            <Field
              component={Form.DatePicker}
              name="reviewDate"
              validate={isRequired}
            />
          </Form.Row>
        )}
        {renderMedicationDetail()}
        {renderStockDetail()}
        {renderSignatureDetail()}
      </StyledFormContentContainer>
      <Form.StickyFooter
        cancelLink={isChildContext
          ? generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.LIST', { childId })
          : generateRoute('SAFEGUARDING.MEDICATION_FORM.LIST')}
        isSubmitting={isSubmitting}
        secondary={{
          disabled: !enableButtons || isSubmitting,
          isLoading: isDraftSubmitting,
          label: i18n.t('global:saveAsDraft'),
          onClick: onSubmitAsDraft,
        }}
        submitDisabled={!enableButtons || isDraftSubmitting}
        submitLabel={i18n.t('global:Save')}
      />
    </Form>
  )
}

const validate = ({ endDate, reviewDate, startDate }: AddMedicationFormVales, props) => {
  const errors = {
    reviewDate: '',
    startDate: '',
  }

  errors.startDate = isStartDateBeforeEndDate(startDate, endDate)

  if (
    props.isLongTerm
    && reviewDate
    && !moment(reviewDate).isBetween(startDate, endDate)
  ) {
    errors.reviewDate = i18n.t('module:Safeguarding:MedicationForm:Add:reviewDateRangeValidation')
  }

  return errors
}

const enhance = compose(
  reduxForm<AddMedicationFormVales, AddFormProps>({ form: ADD_MEDICATION_FORM, validate }),
  connector,
)

export default enhance(AddForm)
